import React from "react";
import { QRCodeSVG } from "qrcode.react";
import FitBox from "./FitBox";
import { FIELD_BY_KEY, PX_PER_MM, fieldValue, fillTokens } from "./canvasModel";

// Placeholder the print step swaps per copy ("Customer Copy", "Pawnee Copy").
export const COPY_LABEL_CLASS = "tc-copy-label-text";

// What goes INSIDE one element's box. Everything here is plain DOM + inline
// styles (plus the MUI sections for blocks), so the print clone needs nothing
// but the copied stylesheets.
const ElementContent = ({ el, ctx, qrValue, images, renderBlock, copyLabel }) => {
  const p = el.props || {};
  const fit = (node) => (
    <FitBox vAlign={el.style.vAlign}>{node}</FitBox>
  );
  switch (el.type) {
    case "field": {
      const def = FIELD_BY_KEY[p.key];
      const value = fieldValue(p.key, ctx);
      const label = (p.label || def?.label || p.key || "").trim();
      return fit(
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {p.showLabel !== false && label && (
            <span style={{ fontWeight: p.labelBold !== false ? 700 : "inherit", color: p.labelColor || "inherit" }}>
              {label}
              {p.labelSep ?? ": "}
            </span>
          )}
          <span>{value}</span>
        </div>,
      );
    }
    case "text":
      return fit(
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {fillTokens(p.text, ctx)}
        </div>,
      );
    case "copyLabel":
      return fit(
        <div className={COPY_LABEL_CLASS} style={{ whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.12em" }}>
          {copyLabel || "Customer Copy"}
        </div>,
      );
    case "qr":
      // Fills the box; a non-square box keeps the code square and centred.
      return qrValue ? (
        <QRCodeSVG
          value={qrValue}
          size={256}
          level={p.level || "L"}
          marginSize={p.quietZone ?? 2}
          style={{ width: "100%", height: "100%", display: "block" }}
        />
      ) : null;
    case "logo":
    case "photo":
    case "image": {
      const src =
        el.type === "logo"
          ? images.logo
          : el.type === "photo"
          ? p.which === "ornament"
            ? images.ornament
            : images.customer
          : p.src;
      if (!src) {
        return (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: "7pt", border: "1px dashed #CBD5E1", boxSizing: "border-box" }} className="tc-editor-only">
            {el.type === "image" ? "No image" : "—"}
          </div>
        );
      }
      return (
        <img
          src={src}
          alt=""
          crossOrigin={src.startsWith("data:") ? undefined : "anonymous"}
          draggable={false}
          style={{ width: "100%", height: "100%", objectFit: p.fit || "contain", display: "block" }}
        />
      );
    }
    case "line": {
      const t = Math.max(0.5, Number(p.thickness) || 1);
      const border = `${t}px ${p.dash || "solid"} ${el.style.color || "#94A3B8"}`;
      return p.direction === "v" ? (
        <div style={{ width: 0, height: "100%", margin: "0 auto", borderLeft: border }} />
      ) : (
        <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
          <div style={{ width: "100%", height: 0, borderTop: border }} />
        </div>
      );
    }
    case "rect":
      return null; // its look is entirely the shared style (border/background)
    case "block":
      return fit(renderBlock ? renderBlock(p.section) : null);
    default:
      return null;
  }
};

// The element's outer frame: position, size, rotation, and the shared style.
export const CanvasElement = ({ el, ctx, qrValue, images, renderBlock, copyLabel, editor }) => {
  const s = el.style || {};
  const isLine = el.type === "line";
  return (
    <div
      className={`tc-el tc-el-${el.type}${editor?.selected ? " tc-selected" : ""}${el.hidden ? " tc-hidden" : ""}${
        // Lets the print step drop the customer's photo from the Customer Copy.
        el.type === "photo" && el.props?.which !== "ornament" ? " tc-cust-photo" : ""
      }`}
      data-el-id={el.id}
      onPointerDown={editor?.onPointerDown}
      onDoubleClick={editor?.onDoubleClick}
      style={{
        position: "absolute",
        left: `${el.x}mm`,
        top: `${el.y}mm`,
        width: `${el.w}mm`,
        height: `${el.h}mm`,
        transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
        zIndex: el.z || 1,
        opacity: el.hidden ? 0.25 : s.opacity ?? 1,
        boxSizing: "border-box",
        cursor: editor ? (el.locked ? "default" : "move") : undefined,
      }}
    >
      <div
        className="tc-el-body"
        style={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          border: !isLine && s.borderWidth ? `${s.borderWidth}px ${s.borderStyle || "solid"} ${s.borderColor}` : undefined,
          borderRadius: s.radius ? `${s.radius}mm` : undefined,
          background: s.bg || undefined,
          padding: isLine || el.type === "qr" ? 0 : `${s.padding ?? 0.5}mm`,
          color: s.color,
          fontSize: `${s.fontSize || 9}pt`,
          fontWeight: s.fontWeight,
          fontStyle: s.fontStyle,
          fontFamily: s.fontFamily || undefined,
          textAlign: s.align,
          lineHeight: s.lineHeight || 1.3,
        }}
      >
        <ElementContent el={el} ctx={ctx} qrValue={qrValue} images={images} renderBlock={renderBlock} copyLabel={copyLabel} />
      </div>
    </div>
  );
};

// One face of a free-form ticket. `scale` only changes how big it LOOKS (the
// sheet itself is always laid out in true mm); the outer box is sized to the
// scaled paper so the page around it flows correctly.
const CanvasSheet = React.forwardRef(
  (
    {
      domId,
      elements,
      paperW,
      paperH,
      scale = 1,
      ctx,
      qrValue,
      images,
      renderBlock,
      copyLabel,
      watermark,
      fontScale = 100,
      showGuides = false,
      gridMm = 0,
      safeMarginMm = 0,
      renderEditorProps,
      onSheetPointerDown,
      children,
    },
    ref,
  ) => {
    const pxW = paperW * PX_PER_MM;
    const pxH = paperH * PX_PER_MM;
    const visible = renderEditorProps ? elements : elements.filter((el) => !el.hidden);
    return (
      <div
        className="tc-sheet-frame"
        style={{ width: pxW * scale, height: pxH * scale, position: "relative", flexShrink: 0 }}
      >
        <div
          ref={ref}
          id={domId}
          className="pawn-ticket-preview tc-sheet"
          onPointerDown={onSheetPointerDown}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: `${paperW}mm`,
            height: `${paperH}mm`,
            transform: scale !== 1 ? `scale(${scale})` : undefined,
            transformOrigin: "top left",
            background: "#fff",
            overflow: "hidden",
            boxShadow: "0 2px 4px rgba(15,23,42,0.06), 0 12px 32px rgba(15,23,42,0.10)",
            fontFamily: "Roboto, sans-serif",
            fontSize: `${fontScale}%`,
            color: "#111827",
          }}
        >
          {showGuides && gridMm > 0 && (
            <div
              className="tc-editor-only"
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                backgroundImage:
                  "linear-gradient(to right, rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(37,99,235,0.07) 1px, transparent 1px)",
                backgroundSize: `${gridMm}mm ${gridMm}mm`,
              }}
            />
          )}
          {showGuides && safeMarginMm > 0 && (
            <div
              className="tc-editor-only"
              title="Printer safe area — most printers cannot print outside it"
              style={{
                position: "absolute",
                left: `${safeMarginMm}mm`,
                top: `${safeMarginMm}mm`,
                right: `${safeMarginMm}mm`,
                bottom: `${safeMarginMm}mm`,
                border: "1px dashed rgba(239,68,68,0.35)",
                pointerEvents: "none",
              }}
            />
          )}
          {watermark?.show && watermark.text && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-45deg)",
                fontSize: "3rem",
                color: "rgba(0,0,0,0.05)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                zIndex: 0,
              }}
            >
              {watermark.text}
            </div>
          )}
          {visible.map((el) => (
            <CanvasElement
              key={el.id}
              el={el}
              ctx={ctx}
              qrValue={qrValue}
              images={images}
              renderBlock={renderBlock}
              copyLabel={copyLabel}
              editor={renderEditorProps ? renderEditorProps(el) : null}
            />
          ))}
          {children}
        </div>
      </div>
    );
  },
);

export default CanvasSheet;
